import { useMemo, useEffect, useRef, memo } from "react"
import { Box, Heading, Text, VStack, Spinner, Center } from "@chakra-ui/react"
import { useLazyQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import {
  GetSystemMonitoringGraphsDocument,
  GetSystemMonitoringGraphsQuery,
  GetSystemMonitoringGraphsQueryVariables,
  type GraphRequest,
} from "@hydrologiq/gql-experience/graphql"
import Dialog from "../../chakra/Dialog"
import type { AlertDataContext, AlertHighlightRange } from "./types"
import { getAlertGraphId } from "./types"
import type { ChartHighlightRange, AssetConfig } from "../../monitoring/builders/2.0.0/graphs/TimeSeries"
import TimeSeriesGraph, {
  calculateDynamicAxisInterval,
  getSeriesColor,
  type TimeSeriesDataPoint,
  type TimeSeriesSeries,
} from "../../monitoring/builders/2.0.0/graphs/TimeSeries"
import StackedBarBreakdownChart from "../../monitoring/builders/2.0.0/graphs/StackedBarBreakdownChart"
import BatteryChart from "../../monitoring/builders/2.0.0/graphs/BatteryChart"
import { DateTime, utc } from "timezonecomplete"
import {
  type GraphCategory,
  deriveGraphCategory,
  getAggregatedColor,
  getGenerationAssetColor,
} from "../../monitoring/builders/2.0.0/graphs/graphColors"
import { hasPhaseData, type PhaseDataPoint } from "../../monitoring/builders/2.0.0/graphs/PhaseBalanceChart"

interface AlertDataPopoverProps {
  open: boolean
  setOpen: (open: boolean) => void
  alertName: string
  dataContext: AlertDataContext
  assetConfigs?: AssetConfig[]
}

type MonitoringRow = GetSystemMonitoringGraphsQuery["getSystemMonitoringGraphs"][number]
type AssetSeries = MonitoringRow["assetBreakdown"][number]
type MonitoringRowWithOverlays = MonitoringRow & { overlays?: MonitoringRow[] }

interface OverlayData {
  graphId: string
  breakdown?: { data: TimeSeriesDataPoint[]; series: TimeSeriesSeries<string[]>[] }
  aggregated?: { data: TimeSeriesDataPoint[]; series: TimeSeriesSeries<string[]>[] }
}

/**
 * Resolve custom asset config for a breakdown series.
 * When `connection` is set, a generic asset-only config must not apply to every connection
 * (otherwise all connections share one label/color). Only an exact assetId+connection
 * match, or no config, applies per-connection series.
 */
function resolveAssetConfigForSeries(
  assetConfigs: AssetConfig[] | undefined,
  assetId: string,
  connection?: string | null
): AssetConfig | undefined {
  if (!assetConfigs?.length) return undefined
  const conn = connection?.trim()
  if (conn) {
    const exact = assetConfigs.find((c) => c.assetId === assetId && c.connection === conn)
    if (exact) return exact
    return undefined
  }
  return (
    assetConfigs.find((c) => c.assetId === assetId && !c.connection) ??
    assetConfigs.find((c) => c.assetId === assetId)
  )
}

function breakdownToChart(
  seriesList: AssetSeries[],
  assetConfigs?: AssetConfig[],
  category?: GraphCategory
): { data: TimeSeriesDataPoint[]; series: TimeSeriesSeries<string[]>[] } {
  const timestampSet = new Set<string>()
  seriesList.forEach((assetSeries) => {
    assetSeries.data.forEach((point: { time: string }) => {
      timestampSet.add(point.time)
    })
  })
  const sortedTimestamps = Array.from(timestampSet).sort()

  const dataPoints: TimeSeriesDataPoint[] = sortedTimestamps.map((timestamp) => {
    const point: TimeSeriesDataPoint = { timestamp }
    seriesList.forEach((assetSeries) => {
      const dataPoint = assetSeries.data.find((d: { time: string }) => d.time === timestamp)
      const seriesKey = assetSeries.connection
        ? `${assetSeries.assetId}_${assetSeries.connection}`
        : assetSeries.assetId
      const value = dataPoint?.value ?? null
      point[seriesKey] = value
      
      // Add peak data support
      const peakKey = `${seriesKey}_peak`
      const peakValue =
        dataPoint && "peakValue" in dataPoint
          ? (dataPoint as { peakValue?: number | null }).peakValue
          : undefined
      point[peakKey] = peakValue !== null && peakValue !== undefined ? peakValue : value
    })
    return point
  })

  const series: TimeSeriesSeries<string[]>[] = seriesList.map((assetSeries, index: number) => {
    const seriesKey = assetSeries.connection
      ? `${assetSeries.assetId}_${assetSeries.connection}`
      : assetSeries.assetId
    const config = resolveAssetConfigForSeries(assetConfigs, assetSeries.assetId, assetSeries.connection)
    const fromTimescale = assetSeries.label?.trim()
    const baseName = fromTimescale || assetSeries.assetName || assetSeries.assetId
    // Use the Timescale label as-is when available (it already includes connection context);
    // only append raw connection ID when no label was provided by the API.
    let label = baseName
    if (assetSeries.connection && !fromTimescale) {
      label = `${baseName} (${assetSeries.connection})`
    }
    // For generation graphs, resolve colour by asset class or name (solar → yellow, generator → purple)
    const generationColor = category === "generation"
      ? getGenerationAssetColor((assetSeries as any).assetClass, baseName)
      : undefined
    return {
      name: seriesKey,
      color: config?.color || generationColor || getSeriesColor(index, seriesList.length, category),
      label: config?.label || label,
      group: config?.group,
      strokeWidth: 2,
      connectNulls: false,
    }
  })

  return { data: dataPoints, series }
}

function aggregatedRowsToChart(
  rows: { time: string; value?: number | null }[],
  category?: GraphCategory
): {
  data: TimeSeriesDataPoint[]
  series: TimeSeriesSeries<string[]>[]
} {
  const data: TimeSeriesDataPoint[] = rows.map((point) => ({
    timestamp: point.time,
    value: point.value ?? null,
  }))
  const aggregatedColor = category ? getAggregatedColor(category) : "#39aed2"
  const series: TimeSeriesSeries<string[]>[] = [
    {
      name: "value",
      color: aggregatedColor,
      label: "Total",
      strokeWidth: 3,
      connectNulls: false,
    },
  ]
  return { data, series }
}

function convertHighlightRanges(ranges?: AlertHighlightRange[]): ChartHighlightRange[] | undefined {
  if (!ranges || ranges.length === 0) return undefined
  return ranges.map((r) => ({
    start: r.start,
    end: r.end,
    label: r.label,
    color: "rgba(255, 165, 0, 0.3)",
  }))
}

function scaleDataPointsWToKw(
  data: TimeSeriesDataPoint[] | undefined,
  sourceUnit?: string,
  desiredUnit?: string
): TimeSeriesDataPoint[] | undefined {
  if (!data || sourceUnit !== "W" || desiredUnit !== "kW") return data
  return data.map((row) => {
    const next: TimeSeriesDataPoint = { ...row }
    for (const key of Object.keys(next)) {
      if (key === "timestamp" || key === "time") continue
      const v = next[key]
      if (typeof v === "number" && Number.isFinite(v)) {
        next[key] = v / 1000
      }
    }
    return next
  })
}

type PopoverGraphMapEntry = {
  breakdown?: { data: TimeSeriesDataPoint[]; series: TimeSeriesSeries<string[]>[] }
  aggregated?: { data: TimeSeriesDataPoint[]; series: TimeSeriesSeries<string[]>[] }
  overlays?: OverlayData[]
  phaseBalance?: PhaseDataPoint[]
}

/** Site load overlay from `power__role_consumer` row — same source as TimescaleTimeSeriesGraph synthetic load */
function buildSyntheticLoadOverlayData(
  graphDataMap: Map<string, PopoverGraphMapEntry>,
  sourceUnit?: string,
  unit?: string
):
  | {
      data: TimeSeriesDataPoint[]
      series: TimeSeriesSeries<string[]>[]
      yAxisLabel: string
      useSameYAxis?: boolean
    }
  | undefined {
  const loadEntry = graphDataMap.get("power__role_consumer")
  if (!loadEntry) return undefined
  const overlaySource = loadEntry.aggregated || loadEntry.breakdown
  if (!overlaySource?.data || overlaySource.series.length === 0) return undefined
  const scaledOverlayData = scaleDataPointsWToKw(overlaySource.data, sourceUnit, unit)
  return {
    data: scaledOverlayData || overlaySource.data,
    series: overlaySource.series
      .filter((s) => s.name !== "combinedPeak")
      .map((s) => ({
        ...s,
        label: s.name === "value" ? "Site load" : s.label,
      })),
    yAxisLabel: "Load (kW)",
    useSameYAxis: true,
  }
}



function getGraphPriority(spec: { role?: string | null; class?: string | null }): number {
  if (spec.role === "generation") return 0
  if (spec.class === "battery") return 1
  if (spec.role === "consumer") return 2
  if (spec.class === "fuel") return 3
  return 4
}

function AlertDataPopoverInternal({
  open,
  setOpen,
  alertName,
  dataContext,
  assetConfigs,
}: AlertDataPopoverProps) {
  const { system_id, time_range, highlight_ranges, graphs } = dataContext
  const hasFetchedRef = useRef(false)
  const lastFetchParamsRef = useRef<string>("")

  const graphRequests: GraphRequest[] = useMemo(() => {
    const requests: GraphRequest[] = []

    graphs.forEach((spec) => {
      const baseGraphId = spec.graph_id || getAlertGraphId(spec)
      const overlayGraphIds = spec.overlay_graph_ids?.length ? spec.overlay_graph_ids : null
      
      const includeConnections =
        spec.include_connections === true ||
        spec.connections_only === true ||
        spec.aggregation_role === "load" ||
        (spec.role === "consumer" && spec.field === "power")
      
      const connectionsOnly = spec.connections_only === true

      if (spec.battery_view && spec.class === "battery") {
        requests.push(
          {
            graphId: `${baseGraphId}_discharge`,
            field: "power",
            role: null,
            assetClass: "battery",
            includeAggregated: false,
            includeBreakdown: true,
            secondaryField: null,
            connectionsOnly: true,
          },
          {
            graphId: `${baseGraphId}_charge`,
            field: "upstream_power",
            role: null,
            assetClass: "battery",
            includeAggregated: false,
            includeBreakdown: true,
            secondaryField: null,
            connectionsOnly: true,
          },
          {
            graphId: `${baseGraphId}_soc`,
            field: "soc",
            role: null,
            assetClass: "battery",
            includeAggregated: false,
            includeBreakdown: true,
            secondaryField: null,
            connectionsOnly: true,
            ...(overlayGraphIds ? { overlayGraphIds } : {}),
          }
        )
      } else {
        const isLoadGraph =
          spec.aggregation_role === "load" ||
          (spec.role === "consumer" && spec.field === "power")
        requests.push({
          graphId: baseGraphId,
          field: spec.field,
          role: spec.role ?? null,
          assetClass: spec.class ?? null,
          includeAggregated: true,
          includeBreakdown: true,
          secondaryField: null,
          includeConnections,
          connectionsOnly,
          ...(spec.aggregation_role ? { aggregationRole: spec.aggregation_role } : {}),
          ...(overlayGraphIds ? { overlayGraphIds } : {}),
          ...(isLoadGraph ? { includePhaseBalance: true } : {}),
        })
      }
      
      // Synthetic load overlay: if this is a generation graph with load overlay,
      // automatically fetch the load data as a separate graph request
      if (spec.role === "generation" && spec.overlay_graph_ids?.includes("power__aggregationRole_load")) {
        requests.push({
          graphId: "power__role_consumer",
          field: "power",
          role: "consumer",
          assetClass: null,
          includeAggregated: true,
          includeBreakdown: true,
          secondaryField: null,
          includeConnections: true,
          connectionsOnly: false,
          aggregationRole: "load",
        })
      }
    })

    return requests
  }, [graphs])

  const [executeQuery, { data, loading, error }] = useLazyQueryWithEndpoint<
    GetSystemMonitoringGraphsQuery,
    GetSystemMonitoringGraphsQueryVariables
  >(
    GetSystemMonitoringGraphsDocument,
    { notifyOnNetworkStatusChange: true },
    "primary"
  )

  useEffect(() => {
    if (!open || graphRequests.length === 0) {
      return
    }

    const fetchParams = JSON.stringify({
      system_id,
      start: time_range.start,
      end: time_range.end,
      graphs: graphRequests,
    })

    if (hasFetchedRef.current && lastFetchParamsRef.current === fetchParams) {
      return
    }

    hasFetchedRef.current = true
    lastFetchParamsRef.current = fetchParams

    executeQuery({
      variables: {
        systemId: system_id,
        start: time_range.start,
        stop: time_range.end,
        granularity: "5m",
        graphs: graphRequests,
      },
    })
  }, [open, system_id, time_range.start, time_range.end, graphRequests, executeQuery])

  useEffect(() => {
    if (!open) {
      hasFetchedRef.current = false
      lastFetchParamsRef.current = ""
    }
  }, [open])

  const chartHighlightRanges = useMemo(
    () => convertHighlightRanges(highlight_ranges),
    [highlight_ranges]
  )

  const startDate = useMemo(() => new DateTime(time_range.start, utc()), [time_range.start])
  const endDate = useMemo(() => new DateTime(time_range.end, utc()), [time_range.end])

  const sharedTimestamps = useMemo(() => {
    const rows = data?.getSystemMonitoringGraphs
    if (!rows?.length) return undefined
    
    const timestampSet = new Set<string>()
    for (const row of rows) {
      if (row.aggregatedData) {
        row.aggregatedData.forEach((point) => {
          if (point.time) timestampSet.add(point.time)
        })
      }
      if (row.assetBreakdown) {
        row.assetBreakdown.forEach((asset) => {
          asset.data.forEach((point: { time: string }) => {
            if (point.time) timestampSet.add(point.time)
          })
        })
      }
    }
    
    return Array.from(timestampSet).sort()
  }, [data])

  const sharedXAxisInterval = useMemo(() => {
    if (!sharedTimestamps?.length) return undefined
    return calculateDynamicAxisInterval(startDate, endDate, sharedTimestamps.length)
  }, [startDate, endDate, sharedTimestamps])

  const graphDataMap = useMemo(() => {
    const map = new Map<string, PopoverGraphMapEntry>()

    const rows = data?.getSystemMonitoringGraphs
    if (!rows?.length) return map

    // Create a map from graphId to category
    const categoryMap = new Map<string, GraphCategory>()
    for (const spec of graphs) {
      const graphId = spec.graph_id || getAlertGraphId(spec)
      const category = deriveGraphCategory({
        role: spec.role ?? undefined,
        class: spec.class ?? undefined,
      })
      categoryMap.set(graphId, category)
    }

    for (const row of rows) {
      const entry = map.get(row.graphId) || {}
      const category = categoryMap.get(row.graphId)

      if (row.aggregatedData && row.aggregatedData.length > 0) {
        entry.aggregated = aggregatedRowsToChart(row.aggregatedData, category)
      }
      if (row.assetBreakdown.length > 0) {
        entry.breakdown = breakdownToChart(row.assetBreakdown, assetConfigs, category)
      }

      // Process overlay data
      const rowWithOverlays = row as MonitoringRowWithOverlays
      if (rowWithOverlays.overlays?.length) {
        entry.overlays = rowWithOverlays.overlays.map((overlay) => {
          let overlayCategory: GraphCategory = "default"
          if (overlay.graphId.includes("__class_battery")) {
            overlayCategory = "battery"
          } else if (overlay.graphId.includes("__role_generation")) {
            overlayCategory = "generation"
          } else if (overlay.graphId.includes("__role_consumer") || overlay.graphId.includes("__aggregationRole_load")) {
            overlayCategory = "load"
          } else if (overlay.graphId.includes("__class_fuel")) {
            overlayCategory = "fuel"
          }

          const overlayData: OverlayData = { graphId: overlay.graphId }
          if (overlay.aggregatedData?.length) {
            overlayData.aggregated = aggregatedRowsToChart(overlay.aggregatedData, overlayCategory)
          }
          if (overlay.assetBreakdown?.length) {
            overlayData.breakdown = breakdownToChart(overlay.assetBreakdown, assetConfigs, overlayCategory)
          }
          return overlayData
        })
      }

      // Keep rows that only have API overlays (e.g. SoC on `_soc` when primary breakdown is empty)
      if (entry.breakdown || entry.aggregated || (entry.overlays && entry.overlays.length > 0)) {
        map.set(row.graphId, entry)
      }

      // Extract phase balance data if available
      const rowAny = row as any
      if (rowAny.phaseBreakdown?.length) {
        const existing = map.get(row.graphId) || entry
        existing.phaseBalance = rowAny.phaseBreakdown.map((point: any) => ({
          timestamp: point.time,
          consumerId: point.connection
            ? `${point.assetId}_${point.connection}`
            : point.assetId,
          phase1: point.phase1 ?? null,
          phase2: point.phase2 ?? null,
          phase3: point.phase3 ?? null,
          phase1Peak: point.phase1Peak ?? null,
          phase2Peak: point.phase2Peak ?? null,
          phase3Peak: point.phase3Peak ?? null,
        }))
        map.set(row.graphId, existing)
      }
    }

    return map
  }, [data, graphs, assetConfigs])

  return (
    <Dialog
      title={`Alert Data: ${alertName}`}
      open={open}
      setOpen={setOpen}
      size="cover"
      footer={<></>}
    >
      <VStack align="stretch" width="100%" height="100%">
        {loading && (
          <Center py={8}>
            <Spinner size="lg" />
          </Center>
        )}

        {error && (
          <Box p={4} bg="red.50" borderRadius="md">
            <Text color="red.600">Failed to load data: {error.message}</Text>
          </Box>
        )}

        {!loading && !error && [...graphs].sort((a, b) => getGraphPriority(a) - getGraphPriority(b)).map((spec) => {
          const baseGraphId = spec.graph_id || getAlertGraphId(spec)

          if (spec.battery_view && spec.class === "battery") {
            const dischargeEntry = graphDataMap.get(`${baseGraphId}_discharge`)
            const chargeEntry = graphDataMap.get(`${baseGraphId}_charge`)
            const socEntry = graphDataMap.get(`${baseGraphId}_soc`)

            if (!dischargeEntry && !chargeEntry && !socEntry) {
              return (
                <Box key={baseGraphId} p={4} bg="gray.50" borderRadius="md">
                  <Heading size="sm" mb={2}>{spec.title}</Heading>
                  <Text color="gray.500">No battery data available</Text>
                </Box>
              )
            }

            const dischargeData = dischargeEntry?.breakdown?.data || dischargeEntry?.aggregated?.data
            const dischargeSeries = dischargeEntry?.breakdown?.series || dischargeEntry?.aggregated?.series || []
            const chargeData = chargeEntry?.breakdown?.data || chargeEntry?.aggregated?.data
            const chargeSeries = chargeEntry?.breakdown?.series || chargeEntry?.aggregated?.series || []
            
            // For battery view, check if SoC overlay data is available
            let socData = socEntry?.breakdown?.data || socEntry?.aggregated?.data
            let socSeries = socEntry?.breakdown?.series || socEntry?.aggregated?.series || []
            
            // If overlay_graph_ids includes SoC, prefer overlay series when it has points (primary row may be empty)
            if (socEntry?.overlays?.length && spec.overlay_graph_ids?.some((id) => id.includes("soc"))) {
              const socOverlay = socEntry.overlays.find((o) => o.graphId.includes("soc"))
              if (socOverlay) {
                const overlaySource = socOverlay.aggregated || socOverlay.breakdown
                if (overlaySource?.data?.length && overlaySource.series?.length) {
                  socData = overlaySource.data
                  socSeries = overlaySource.series
                }
              }
            }

            const scaledDischarge = scaleDataPointsWToKw(dischargeData, spec.source_unit, spec.unit)
            const scaledCharge = scaleDataPointsWToKw(chargeData, spec.source_unit, spec.unit)

            return (
              <Box key={baseGraphId} height="100%">
                {spec.annotation && (
                  <Box mb={2} p={2} bg="orange.50" borderRadius="md" borderLeftWidth="3px" borderLeftColor="orange.400">
                    <Text fontSize="sm" color="orange.800" whiteSpace={"pre-line"}>{spec.annotation}</Text>
                  </Box>
                )}
                <BatteryChart
                  dischargeData={scaledDischarge || []}
                  dischargeSeries={dischargeSeries}
                  chargeData={scaledCharge || []}
                  chargeSeries={chargeSeries}
                  socData={socData || []}
                  socSeries={socSeries}
                  graph={{
                    title: spec.title,
                    yAxis: spec.unit ? `${spec.title} (${spec.unit})` : spec.title,
                    type: "TimeSeries",
                    size: "full",
                    source: {
                      field: spec.field,
                      type: "TimescaleDB",
                      sourceUnit: spec.source_unit as any,
                      desiredUnit: spec.unit as any,
                    },
                    versioning: { schema: "1.0.0", revision: 1 },
                  }}
                  chartAxisStartDate={startDate}
                  chartAxisEndDate={endDate}
                  highlightRanges={chartHighlightRanges}
                  sharedTimestamps={sharedTimestamps}
                  xAxisTickInterval={sharedXAxisInterval}
                />
              </Box>
            )
          }

          const chartEntry = graphDataMap.get(baseGraphId)
          if (!chartEntry || (!chartEntry.breakdown && !chartEntry.aggregated)) {
            return (
              <Box key={baseGraphId} p={4} bg="gray.50" borderRadius="md">
                <Heading size="sm" mb={2}>{spec.title}</Heading>
                <Text color="gray.500">No data available</Text>
              </Box>
            )
          }

          const breakdownData = chartEntry.breakdown?.data
          const breakdownSeries = chartEntry.breakdown?.series || []
          const aggregatedData = chartEntry.aggregated?.data

          const scaledBreakdown = scaleDataPointsWToKw(breakdownData, spec.source_unit, spec.unit)
          const scaledAggregated = scaleDataPointsWToKw(aggregatedData, spec.source_unit, spec.unit)

          if (spec.use_stacked_bar) {

            let stackedOverlayData:
              | { data: TimeSeriesDataPoint[]; series: TimeSeriesSeries<string[]>[]; yAxisLabel: string }
              | undefined
            if (spec.role === "generation" && spec.overlay_graph_ids?.includes("power__aggregationRole_load")) {
              stackedOverlayData = buildSyntheticLoadOverlayData(graphDataMap, spec.source_unit, spec.unit)
            }
            if (!stackedOverlayData && chartEntry.overlays?.length && spec.overlay_graph_ids?.length) {
              const firstOverlay = chartEntry.overlays[0]
              const overlaySource = firstOverlay.aggregated || firstOverlay.breakdown
              if (overlaySource) {
                stackedOverlayData = {
                  data: overlaySource.data,
                  series: overlaySource.series,
                  yAxisLabel: firstOverlay.graphId.includes("soc") ? "SoC (%)" : "Power (kW)",
                }
              }
            }

            return (
              <Box key={baseGraphId} height="100%">
                {spec.annotation && (
                  <Box mb={2} p={2} bg="orange.50" borderRadius="md" borderLeftWidth="3px" borderLeftColor="orange.400">
                    <Text fontSize="sm" color="orange.800" whiteSpace={"pre-line"}>{spec.annotation}</Text>
                  </Box>
                )}
                <StackedBarBreakdownChart
                  data={scaledBreakdown || breakdownData || []}
                  series={breakdownSeries}
                  aggregatedData={scaledAggregated}
                  graph={{
                    title: spec.title,
                    yAxis: spec.unit ? `${spec.title} (${spec.unit})` : spec.title,
                    type: "TimeSeries",
                    size: "full",
                    source: {
                      field: spec.field,
                      type: "TimescaleDB",
                      sourceUnit: spec.source_unit as any,
                      desiredUnit: spec.unit as any,
                    },
                    versioning: { schema: "1.0.0", revision: 1 },
                  }}
                  showBackToggle={false}
                  onBack={() => { }}
                  showPeakToggle={true}
                  defaultShowPeaks={false}
                  chartHeight="350px"
                  highlightRanges={chartHighlightRanges}
                  sharedTimestamps={sharedTimestamps}
                  overlayData={stackedOverlayData}
                  xAxisTickInterval={sharedXAxisInterval}
                  chartAxisStartDate={startDate}
                  chartAxisEndDate={endDate}
                  phaseBalanceData={chartEntry.phaseBalance}
                  phaseDataAvailable={chartEntry.phaseBalance ? hasPhaseData(chartEntry.phaseBalance) : false}
                  highlightPhase={spec.highlight_phase}
                />
              </Box>
            )
          }

          const lineData = chartEntry.breakdown?.data || chartEntry.aggregated?.data || []
          const lineSeries = chartEntry.breakdown?.series || chartEntry.aggregated?.series || []
          const scaledLineData = scaleDataPointsWToKw(lineData, spec.source_unit, spec.unit)

          // Prepare overlay data if available
          let overlayData: { data: TimeSeriesDataPoint[]; series: TimeSeriesSeries<string[]>[]; yAxisLabel: string } | undefined
          
          // Synthetic site load overlay for Power Generation (mirrors TimescaleTimeSeriesGraph logic)
          const isGenerationGraph = spec.role === "generation"
          if (isGenerationGraph && spec.overlay_graph_ids?.includes("power__aggregationRole_load")) {
            overlayData = buildSyntheticLoadOverlayData(graphDataMap, spec.source_unit, spec.unit)
          }
          
          // Standard overlay pipeline (e.g. battery-soc)
          if (!overlayData && chartEntry.overlays?.length && spec.overlay_graph_ids?.length) {
            const firstOverlay = chartEntry.overlays[0]
            const overlaySource = firstOverlay.aggregated || firstOverlay.breakdown
            if (overlaySource) {
              overlayData = {
                data: overlaySource.data,
                series: overlaySource.series,
                yAxisLabel: firstOverlay.graphId.includes("soc") ? "SoC (%)" : "Power (kW)",
              }
            }
          }

          return (
            <Box key={baseGraphId} height="100%">
              {spec.annotation && (
                <Box mb={2} p={2} bg="orange.50" borderRadius="md" borderLeftWidth="3px" borderLeftColor="orange.400">
                  <Text fontSize="sm" color="orange.800" whiteSpace={"pre-line"}>{spec.annotation}</Text>
                </Box>
              )}
              <TimeSeriesGraph
                data={scaledLineData || lineData}
                series={lineSeries}
                graph={{
                  title: spec.title,
                  yAxis: spec.unit ? `${spec.title} (${spec.unit})` : spec.title,
                  type: "TimeSeries",
                  size: "full",
                  source: {
                    field: spec.field,
                    type: "TimescaleDB",
                    sourceUnit: spec.source_unit as any,
                    desiredUnit: spec.unit as any,
                  },
                  versioning: { schema: "1.0.0", revision: 1 },
                }}
                startDate={startDate}
                endDate={endDate}
                height="350px"
                highlightRanges={chartHighlightRanges}
                hideAssetMenu
                overlayData={overlayData}
                sharedTimestamps={sharedTimestamps}
                xAxisTickInterval={sharedXAxisInterval}
              />
            </Box>
          )
        })}

        {!loading && !error && graphDataMap.size === 0 && (
          <Box p={4} bg="gray.50" borderRadius="md">
            <Text color="gray.500">No graph data available for this alert.</Text>
          </Box>
        )}
      </VStack>
    </Dialog>
  )
}

function arePopoverPropsEqual(prevProps: AlertDataPopoverProps, nextProps: AlertDataPopoverProps): boolean {
  if (prevProps.open !== nextProps.open) return false
  if (prevProps.setOpen !== nextProps.setOpen) return false
  if (prevProps.alertName !== nextProps.alertName) return false

  const prevCtx = prevProps.dataContext
  const nextCtx = nextProps.dataContext

  if (prevCtx.system_id !== nextCtx.system_id) return false
  if (prevCtx.time_range.start !== nextCtx.time_range.start) return false
  if (prevCtx.time_range.end !== nextCtx.time_range.end) return false

  if (prevCtx.graphs.length !== nextCtx.graphs.length) return false

  if (prevProps.assetConfigs !== nextProps.assetConfigs) return false

  return true
}

export const AlertDataPopover = memo(AlertDataPopoverInternal, arePopoverPropsEqual)
